/**
 * CRYIN — Cart Component JS
 * Quantity updates and item removal via AJAX with animations.
 * No full page reloads — everything updates in place.
 */

(function () {
  'use strict';

  const cartPage = document.querySelector('[data-cart-page]');
  if (!cartPage) return;

  let isUpdating = false;

  /* -----------------------------------------------------------------------
     Update Cart Item — AJAX with animated UI feedback
     ----------------------------------------------------------------------- */

  async function updateCartItem(line, quantity, itemEl) {
    if (isUpdating) return;
    isUpdating = true;

    // If removing (quantity 0), animate item out
    const isRemoving = quantity === 0;

    try {
      const response = await fetch(window.CRYIN.routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });

      if (!response.ok) throw new Error('Cart update failed');
      const cart = await response.json();

      if (isRemoving && itemEl) {
        // Animate item sliding out
        itemEl.style.transition = 'opacity 0.35s ease, transform 0.35s ease, max-height 0.4s ease 0.1s';
        itemEl.style.opacity = '0';
        itemEl.style.transform = 'translateX(-20px)';
        itemEl.style.maxHeight = itemEl.offsetHeight + 'px';
        itemEl.offsetHeight; // force reflow
        itemEl.style.maxHeight = '0';
        itemEl.style.overflow = 'hidden';
        itemEl.style.paddingTop = '0';
        itemEl.style.paddingBottom = '0';

        await new Promise(resolve => setTimeout(resolve, 450));
        itemEl.remove();
      } else if (itemEl) {
        // Update quantity display with bump animation
        const qtyValue = itemEl.querySelector('[data-quantity-input]');
        const hidden = itemEl.querySelector('[data-quantity-hidden]');
        if (qtyValue) {
          qtyValue.textContent = quantity;
          qtyValue.classList.add('is-bumping');
          qtyValue.addEventListener('animationend', () => {
            qtyValue.classList.remove('is-bumping');
          }, { once: true });
        }
        if (hidden) hidden.value = quantity;

        // Update line price
        const priceEl = itemEl.querySelector('.cart-item__price');
        if (priceEl) {
          const lineItem = cart.items.find((it, i) => i === line - 1);
          if (lineItem) {
            priceEl.style.transition = 'opacity 0.2s ease';
            priceEl.style.opacity = '0.3';
            setTimeout(() => {
              priceEl.textContent = window.CRYIN.formatMoney(lineItem.final_line_price);
              priceEl.style.opacity = '1';
            }, 200);
          }
        }
      }

      // Update subtotal with fade
      const subtotalEl = document.querySelector('[data-cart-subtotal]');
      if (subtotalEl) {
        subtotalEl.style.transition = 'opacity 0.2s ease';
        subtotalEl.style.opacity = '0.3';
        setTimeout(() => {
          subtotalEl.textContent = window.CRYIN.formatMoney(cart.total_price);
          subtotalEl.style.opacity = '1';
        }, 200);
      }

      // Update cart count in header
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.classList.remove('is-bumping');
        el.offsetHeight;
        el.classList.add('is-bumping');
        el.addEventListener('animationend', () => el.classList.remove('is-bumping'), { once: true });
      });

      // If cart is now empty, show empty state with animation
      if (cart.item_count === 0) {
        const form = cartPage.querySelector('[data-cart-form]');
        if (form) {
          form.style.transition = 'opacity 0.35s ease';
          form.style.opacity = '0';
          await new Promise(resolve => setTimeout(resolve, 350));

          // Replace with empty state — strings come from window.CRYIN.strings (populated from locale)
          const strings = window.CRYIN.strings || {};
          const emptyText = (strings.cartEmpty || 'Cart is empty').toUpperCase();
          const continueText = (strings.continueShopping || 'Continue shopping').toUpperCase();
          const continueUrl = (window.CRYIN.routes && window.CRYIN.routes.root) || '/';
          const emptyDiv = document.createElement('div');
          emptyDiv.className = 'cart-page__empty';
          emptyDiv.style.opacity = '0';
          emptyDiv.style.transform = 'translateY(12px)';
          emptyDiv.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
          const p = document.createElement('p');
          p.className = 'cart-page__empty-text';
          p.textContent = emptyText;
          const a = document.createElement('a');
          a.href = continueUrl;
          a.className = 'cart-page__continue';
          a.textContent = continueText;
          emptyDiv.appendChild(p);
          emptyDiv.appendChild(a);
          form.replaceWith(emptyDiv);
          emptyDiv.offsetHeight;
          emptyDiv.style.opacity = '1';
          emptyDiv.style.transform = 'translateY(0)';
        }
      }

    } catch (error) {
      // On error, reload as fallback
      window.location.reload();
    } finally {
      isUpdating = false;
    }
  }

  /* -----------------------------------------------------------------------
     Quantity +/- Buttons
     ----------------------------------------------------------------------- */

  cartPage.addEventListener('click', (e) => {
    const minusBtn = e.target.closest('[data-quantity-minus]');
    const plusBtn = e.target.closest('[data-quantity-plus]');
    const removeBtn = e.target.closest('[data-remove-item]');

    if (minusBtn) {
      e.preventDefault();
      const item = minusBtn.closest('.cart-item');
      const hidden = item.querySelector('[data-quantity-hidden]');
      if (!hidden) return;
      const line = parseInt(hidden.dataset.line, 10);
      const qty = parseInt(hidden.value, 10);
      if (line && qty > 0) {
        updateCartItem(line, qty - 1, item);
      }
    }

    if (plusBtn) {
      e.preventDefault();
      const item = plusBtn.closest('.cart-item');
      const hidden = item.querySelector('[data-quantity-hidden]');
      if (!hidden) return;
      const line = parseInt(hidden.dataset.line, 10);
      const qty = parseInt(hidden.value, 10);
      if (line) {
        updateCartItem(line, qty + 1, item);
      }
    }

    if (removeBtn) {
      e.preventDefault();
      const item = removeBtn.closest('.cart-item');
      const line = parseInt(removeBtn.dataset.line, 10);
      if (line) {
        updateCartItem(line, 0, item);
      }
    }
  });

})();
